// rtc.js — WebRTC connection management for Peerly
// Handles peer connection, ICE, media streams, data channel, audio visualizer.

import { loadIceServers, ICE_RESTART, BITRATE, PROTOCOL } from './config.js';
import { getMutableState, update, Phase, Role } from './state.js';
import { parseIncoming } from './protocol.js';
import * as signaling from './signaling.js';
import { showNotification } from './utils.js';

let _onRemoteTrack = null;
let _onDataMessage = null;
let _onConnectionStateChange = null;
let _onDataChannelStateChange = null;
let _onQualityUpdate = null;
let _qualityMonitorId = null;
let _restartTimer = null;
let _restartAttempts = 0;
let _currentVideoBitrate = BITRATE.VIDEO_MAX; // eslint-disable-line no-unused-vars
let _lastQuality = 'good';
let _roomListenerSetup = null; // resubscribe fn for ICE restart room listener
let _tearingDown = false;

/** Active incoming file transfers being assembled */
const _incomingFiles = new Map();

/** Pending outbound file sends awaiting peer acceptance */
const _pendingFileSends = new Map();

/** Callback for file transfer progress updates */
let _onFileProgress = null;
let _onFileComplete = null;
let _onFileRequest = null;

/** Local video element ref for screen share display (set from app.js) */
let DOM_localVideo = null;

/**
 * Register callbacks for RTC events.
 */
export function setCallbacks({
  onRemoteTrack,
  onDataMessage,
  onConnectionStateChange,
  onDataChannelStateChange,
  onQualityUpdate,
}) {
  _onRemoteTrack = onRemoteTrack;
  _onDataMessage = onDataMessage;
  _onConnectionStateChange = onConnectionStateChange;
  _onDataChannelStateChange = onDataChannelStateChange;
  _onQualityUpdate = onQualityUpdate;
}

/**
 * Initialize local media (camera + mic).
 * Preserves mute/camera state across device switches.
 * Old tracks are stopped only after successful swap.
 *
 * @param {string|null} videoDeviceId
 * @param {string|null} audioDeviceId
 * @returns {Promise<MediaStream|null>}
 */
export async function initializeMedia(videoDeviceId = null, audioDeviceId = null) {
  const state = getMutableState();
  const oldStream = state.localStream;

  const constraints = {
    video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true,
    audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
  };

  let newStream;
  try {
    newStream = await navigator.mediaDevices.getUserMedia(constraints);
  } catch (error) {
    console.error('[rtc] Error accessing media devices:', error);
    showNotification('Camera/Microphone access denied', 'error');
    return null;
  }

  // Apply current mute/video state to new tracks
  const videoTrack = newStream.getVideoTracks()[0];
  const audioTrack = newStream.getAudioTracks()[0];

  if (videoTrack) {
    videoTrack.enabled = state.isVideoEnabled;
  }
  if (audioTrack) {
    audioTrack.enabled = state.isAudioEnabled;
  }

  // Replace tracks on existing peer connection if active
  if (state.peerConnection) {
    try {
      const senders = state.peerConnection.getSenders();
      const videoSender = senders.find((s) => s.track?.kind === 'video');
      const audioSender = senders.find((s) => s.track?.kind === 'audio');

      if (videoSender && videoTrack) {
        await videoSender.replaceTrack(videoTrack);
      }
      if (audioSender && audioTrack) {
        await audioSender.replaceTrack(audioTrack);
      }
    } catch (err) {
      console.warn('[rtc] Failed to replace tracks on peer connection:', err);
    }
  }

  // Stop old tracks only after successful replacement
  if (oldStream) {
    cancelVisualizer();
    oldStream.getTracks().forEach((track) => track.stop());
  }

  update({ localStream: newStream });
  return newStream;
}

/**
 * Populate device lists (cameras and microphones).
 * Returns { videoDevices, audioDevices }
 */
export async function getDevices() {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return {
      videoDevices: devices.filter((d) => d.kind === 'videoinput'),
      audioDevices: devices.filter((d) => d.kind === 'audioinput'),
    };
  } catch (err) {
    console.warn('[rtc] Failed to enumerate devices:', err);
    return { videoDevices: [], audioDevices: [] };
  }
}

/**
 * Start a pre-call device preview stream.
 * Returns the stream and device lists, or null on failure.
 */
export async function startPreview(videoDeviceId = null) {
  const constraints = {
    video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true,
    audio: false, // Don't request audio for preview — avoids echo
  };

  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia(constraints);
  } catch (error) {
    console.error('[rtc] Preview media access denied:', error);
    return null;
  }

  update({ previewStream: stream });

  // Enumerate devices after permission granted (labels available)
  const { videoDevices, audioDevices } = await getDevices();
  return { stream, videoDevices, audioDevices };
}

/**
 * Switch the preview to a different camera.
 */
export async function switchPreviewCamera(videoDeviceId) {
  const state = getMutableState();
  if (state.previewStream) {
    state.previewStream.getTracks().forEach((t) => t.stop());
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: videoDeviceId } },
      audio: false,
    });
    update({ previewStream: stream, previewVideoDeviceId: videoDeviceId });
    return stream;
  } catch (err) {
    console.warn('[rtc] Failed to switch preview camera:', err);
    return null;
  }
}

/**
 * Stop the preview stream.
 */
export function stopPreview() {
  const state = getMutableState();
  if (state.previewStream) {
    state.previewStream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {}
    });
    update({ previewStream: null });
  }
}

/**
 * Create and configure a new RTCPeerConnection.
 * Sets up ICE candidate handling, track handling, data channel, and connection state monitoring.
 */
export async function createPeerConnection() {
  const state = getMutableState();
  const iceConfig = await loadIceServers();

  const pc = new RTCPeerConnection(iceConfig);
  update({ peerConnection: pc });

  // Add local tracks
  if (state.localStream) {
    state.localStream.getTracks().forEach((track) => {
      pc.addTrack(track, state.localStream);
    });
  }

  // Handle remote tracks
  pc.ontrack = (event) => {
    const remoteStream = event.streams[0];
    update({ remoteStream });
    if (_onRemoteTrack) _onRemoteTrack(remoteStream);
  };

  // ICE candidate handling — batch locally, flush on timeout or gathering complete
  pc.onicecandidate = (event) => {
    if (event.candidate) {
      signaling.addCandidate(state.roomId, state.role, event.candidate);
    }
  };

  // Flush candidate batch when ICE gathering completes
  pc.addEventListener('icegatheringstatechange', () => {
    if (pc.iceGatheringState === 'complete') {
      signaling.flushCandidateBatch();
    }
  });

  // ICE connection state
  pc.oniceconnectionstatechange = () => {
    update({ iceConnectionState: pc.iceConnectionState });

    if (pc.iceConnectionState === 'failed') {
      showNotification('Connection failed — check your network or try a different link', 'error');
      update({ phase: Phase.FAILED, lastError: 'ICE connection failed' });
    }

    // If ICE disconnects and data channel is also closed, trigger restart immediately
    if (pc.iceConnectionState === 'disconnected') {
      const state = getMutableState();
      if (state.phase === Phase.CONNECTED) {
        const dc = state.dataChannel;
        if (!dc || dc.readyState !== 'open') {
          update({ phase: Phase.RECONNECTING });
          scheduleIceRestart(pc);
        }
      }
    }
  };

  // Connection state (covers more states than ICE alone)
  pc.onconnectionstatechange = () => {
    const connState = pc.connectionState;

    if (_onConnectionStateChange) _onConnectionStateChange(connState);

    switch (connState) {
      case 'connected':
        update({ phase: Phase.CONNECTED });
        startQualityMonitor(pc);
        clearRestartTimer();
        _restartAttempts = 0;
        break;
      case 'disconnected':
        if (state.phase === Phase.CONNECTED) {
          update({ phase: Phase.RECONNECTING });
          scheduleIceRestart(pc);
        }
        stopQualityMonitor();
        break;
      case 'failed':
        update({ phase: Phase.FAILED, lastError: 'Peer connection failed' });
        stopQualityMonitor();
        clearRestartTimer();
        break;
      case 'closed':
        // Only move to ended if we were connected (not if we're already in teardown)
        if (state.phase === Phase.CONNECTED || state.phase === Phase.RECONNECTING) {
          update({ phase: Phase.ENDED });
        }
        stopQualityMonitor();
        clearRestartTimer();
        break;
    }
  };

  // Data channel setup
  if (state.role === Role.SENDER) {
    const dc = pc.createDataChannel('chat');
    update({ dataChannel: dc });
    setupDataChannel(dc);
  } else {
    pc.ondatachannel = (event) => {
      const dc = event.channel;
      update({ dataChannel: dc });
      setupDataChannel(dc);
    };
  }

  return pc;
}

/**
 * Set up data channel event handlers with protocol validation.
 */
function setupDataChannel(dc) {
  dc.onopen = () => {
    update({ dataChannelState: 'open' });
    if (_onDataChannelStateChange) _onDataChannelStateChange('open');

    // Send our nickname on channel open
    const state = getMutableState();
    sendData({ type: 'nickname', nickname: state.localNickname });
  };

  dc.onmessage = (event) => {
    if (_tearingDown) return;
    // Validate all incoming messages through protocol module
    const result = parseIncoming(event.data);
    if (!result.valid) {
      console.warn('[rtc] Rejected peer message:', result.error);
      return;
    }
    if (_onDataMessage) _onDataMessage(result.data);
  };

  dc.onclose = () => {
    if (_tearingDown) return;
    update({ dataChannelState: 'closed' });
    if (_onDataChannelStateChange) _onDataChannelStateChange('closed');
  };

  dc.onerror = (err) => {
    console.warn('[rtc] DataChannel error:', err);
    update({ dataChannelState: 'error' });
  };
}

/**
 * Send data through the data channel.
 * Returns true if sent, false if channel not open.
 */
export function sendData(payload) {
  const state = getMutableState();
  if (state.dataChannel && state.dataChannel.readyState === 'open') {
    try {
      state.dataChannel.send(JSON.stringify(payload));
      return true;
    } catch (err) {
      console.warn('[rtc] Failed to send data:', err);
      return false;
    }
  }
  return false;
}

/**
 * Execute the sender call flow:
 * 1. Create offer
 * 2. Set local description
 * 3. Write offer to Firestore
 * 4. Listen for answer
 * 5. Listen for remote candidates
 */
export async function startSenderFlow(roomId) {
  const state = getMutableState();
  const pc = state.peerConnection;
  if (!pc) throw new Error('No peer connection');

  update({ phase: Phase.CREATING_ROOM });

  // Create offer
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  // Write to Firestore
  const result = await signaling.createRoom(roomId, offer);
  if (!result.success) {
    return result;
  }

  update({ phase: Phase.WAITING_FOR_PEER, isRoomOwner: true });

  // Start heartbeat
  const cancelHeartbeat = signaling.startHeartbeat(roomId);
  update({ heartbeatTimer: cancelHeartbeat });

  // Listen for answer
  let answerApplied = false;
  let lastAnswerSdp = null;
  const onAnswer = async (answer) => {
    if (answer.sdp === lastAnswerSdp) return;
    lastAnswerSdp = answer.sdp;
    if (answerApplied && !pc.currentRemoteDescription) return;

    try {
      if (pc.currentRemoteDescription) {
        // ICE restart: apply new answer
        console.info('[rtc] Received restart answer, applying');
        update({ phase: Phase.CONNECTING });
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
      } else {
        // Initial answer
        update({ phase: Phase.CONNECTING });
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
        answerApplied = true;
        // Unsubscribe room listener to save Firestore reads during steady-state
        // Will resubscribe in scheduleIceRestart if needed
        unsubRoom();
        update({ roomListener: null });
      }
    } catch (err) {
      console.error('[rtc] Failed to set remote description:', err);
      update({ lastError: 'Failed to process answer from peer' });
    }
  };
  _roomListenerSetup = () => {
    const unsub = signaling.listenForAnswer(roomId, onAnswer);
    update({ roomListener: unsub });
    return unsub;
  };
  const unsubRoom = _roomListenerSetup();
  update({ roomListener: unsubRoom });

  // Listen for callee candidates
  const unsubCandidates = signaling.listenForCandidates(
    roomId,
    Role.SENDER,
    async (candidateData) => {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidateData));
      } catch (err) {
        console.warn('[rtc] Failed to add remote ICE candidate:', err);
      }
    },
  );
  update({ candidatesListener: unsubCandidates });

  return { success: true };
}

/**
 * Execute the receiver call flow:
 * 1. Get offer from Firestore
 * 2. Set remote description
 * 3. Create and set answer
 * 4. Write answer to Firestore
 * 5. Listen for caller candidates
 */
export async function startReceiverFlow(roomId) {
  const state = getMutableState();
  const pc = state.peerConnection;
  if (!pc) throw new Error('No peer connection');

  update({ phase: Phase.JOINING_ROOM });

  // Join room and get offer
  const joinResult = await signaling.joinRoom(roomId);
  if (!joinResult.success) {
    return joinResult;
  }

  // Receiver does not heartbeat — only the room owner (sender) does.
  // This halves heartbeat writes and reduces Firestore quota usage.

  // Set remote description from offer
  let answerCreated = false;
  let lastOfferSdp = null;
  const onOffer = async (offer) => {
    if (offer.sdp === lastOfferSdp) return;
    lastOfferSdp = offer.sdp;
    if (answerCreated && !pc.currentRemoteDescription) return;

    try {
      if (pc.currentRemoteDescription) {
        // ICE restart: we already have a remote description, this is a new offer
        console.info('[rtc] Received restart offer, creating new answer');
        update({ phase: Phase.CONNECTING });
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await signaling.writeAnswer(roomId, answer);
      } else {
        // Initial offer
        update({ phase: Phase.CONNECTING });
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await signaling.writeAnswer(roomId, answer);
        answerCreated = true;
        // Unsubscribe room listener to save Firestore reads during steady-state
        // Will resubscribe in scheduleIceRestart if needed
        unsubRoom();
        update({ roomListener: null });
      }
    } catch (err) {
      console.error('[rtc] Failed to complete receiver handshake:', err);
      update({ phase: Phase.FAILED, lastError: 'Failed to connect to peer' });
    }
  };
  _roomListenerSetup = () => {
    const unsub = signaling.listenForOffer(roomId, onOffer);
    update({ roomListener: unsub });
    return unsub;
  };
  const unsubRoom = _roomListenerSetup();
  update({ roomListener: unsubRoom });

  // Listen for caller candidates
  const unsubCandidates = signaling.listenForCandidates(
    roomId,
    Role.RECEIVER,
    async (candidateData) => {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidateData));
      } catch (err) {
        console.warn('[rtc] Failed to add remote ICE candidate:', err);
      }
    },
  );
  update({ candidatesListener: unsubCandidates });

  return { success: true };
}

/**
 * Start monitoring connection quality using getStats().
 * Polls every 5s for packet loss and round-trip time.
 */
function startQualityMonitor(pc) {
  stopQualityMonitor();
  _qualityMonitorId = setInterval(async () => {
    if (_tearingDown || !pc || pc.connectionState !== 'connected') {
      stopQualityMonitor();
      return;
    }
    try {
      const stats = await pc.getStats();
      let rtt = null;
      let packetsLost = 0;
      let packetsReceived = 0;

      stats.forEach((report) => {
        if (report.type === 'candidate-pair' && report.nominated) {
          rtt = report.currentRoundTripTime;
        }
        if (report.type === 'inbound-rtp') {
          packetsLost += report.packetsLost || 0;
          packetsReceived += report.packetsReceived || 0;
        }
      });

      const lossRate = packetsReceived > 0 ? packetsLost / packetsReceived : 0;
      const rttMs = rtt !== null && rtt !== undefined ? rtt * 1000 : null;

      let quality = 'good';
      if ((rttMs !== null && rttMs > 400) || lossRate > 0.1) {
        quality = 'poor';
      } else if ((rttMs !== null && rttMs > 200) || lossRate > 0.03) {
        quality = 'weak';
      }

      adaptBandwidth(quality);
      if (_onQualityUpdate) _onQualityUpdate(quality);
    } catch {
      // Stats not available — silently skip
    }
  }, 5000);
}

/**
 * Stop the quality monitor.
 */
function stopQualityMonitor() {
  if (_qualityMonitorId) {
    clearInterval(_qualityMonitorId);
    _qualityMonitorId = null;
  }
  if (_onQualityUpdate && !_tearingDown) _onQualityUpdate(null);
}

/**
 * Schedule an ICE restart after a delay.
 * If the connection recovers before the timer fires, it's cleared.
 * Uses exponential backoff for retry intervals.
 */
function scheduleIceRestart(pc) {
  clearRestartTimer();

  _restartTimer = setTimeout(async () => {
    if (_restartAttempts >= ICE_RESTART.MAX_ATTEMPTS) {
      console.warn('[rtc] Max ICE restart attempts reached, giving up');
      update({ phase: Phase.FAILED, lastError: 'Could not reconnect to peer' });
      clearRestartTimer();
      return;
    }

    if (pc.connectionState === 'connected') {
      clearRestartTimer();
      return;
    }

    _restartAttempts++;
    console.info(`[rtc] ICE restart attempt ${_restartAttempts}/${ICE_RESTART.MAX_ATTEMPTS}`);

    // Resubscribe room listener to receive restart offer/answer
    if (_roomListenerSetup) {
      _roomListenerSetup();
    }

    try {
      pc.restartIce();
      const state = getMutableState();
      if (state.role === Role.SENDER) {
        const offer = await pc.createOffer({ iceRestart: true });
        await pc.setLocalDescription(offer);
        await signaling.writeOffer(state.roomId, offer);
      }
      // Receiver doesn't need to initiate — it will receive the new offer
      // via the existing listener and respond with a new answer.
    } catch (err) {
      console.warn('[rtc] ICE restart failed:', err);
      if (_restartAttempts < ICE_RESTART.MAX_ATTEMPTS) {
        const backoff = Math.min(
          ICE_RESTART.RETRY_INTERVAL_MS * Math.pow(ICE_RESTART.BACKOFF_MULTIPLIER, _restartAttempts - 1),
          ICE_RESTART.MAX_BACKOFF_MS,
        );
        console.info(`[rtc] Scheduling retry in ${Math.round(backoff / 1000)}s (backoff)`);
        _restartTimer = setTimeout(() => scheduleIceRestart(pc), backoff);
      }
    }
  }, ICE_RESTART.RESTART_DELAY_MS);

  update({ restartTimer: _restartTimer });
}

/**
 * Public wrapper to request an ICE restart from outside rtc.js (e.g. app.js).
 * Only triggers if we have an active peer connection and aren't already restarting.
 */
export function requestIceRestart() {
  const state = getMutableState();
  if (!state.peerConnection || _tearingDown) return;
  if (state.phase === Phase.RECONNECTING) return; // already in progress
  if (state.peerConnection.connectionState === 'connected') return;
  update({ phase: Phase.RECONNECTING });
  scheduleIceRestart(state.peerConnection);
}

/**
 * Clear any pending ICE restart timer.
 */
function clearRestartTimer() {
  if (_restartTimer) {
    clearTimeout(_restartTimer);
    _restartTimer = null;
    update({ restartTimer: null });
  }
}

/**
 * Apply a bitrate limit to the video sender via setParameters().
 * @param {number} maxBitrate — in bits per second
 */
async function applyVideoBitrate(maxBitrate) {
  const state = getMutableState();
  if (!state.peerConnection) return;

  const sender = state.peerConnection.getSenders().find((s) => s.track?.kind === 'video');
  if (!sender) return;

  const params = sender.getParameters();
  if (!params.encodings || params.encodings.length === 0) {
    params.encodings = [{}];
  }
  params.encodings[0].maxBitrate = maxBitrate;
  try {
    await sender.setParameters(params);
    _currentVideoBitrate = maxBitrate;
    console.info(`[rtc] Video bitrate set to ${maxBitrate / 1000}kbps`);
  } catch (err) {
    console.warn('[rtc] Failed to set video bitrate:', err);
  }
}

/**
 * Adapt bandwidth based on quality level.
 * Reduces bitrate on degradation, restores on improvement.
 */
function adaptBandwidth(quality) {
  if (_tearingDown || quality === _lastQuality) return;
  const prevQuality = _lastQuality;
  _lastQuality = quality;

  if (quality === 'poor') {
    applyVideoBitrate(BITRATE.VIDEO_MINIMAL);
  } else if (quality === 'weak') {
    applyVideoBitrate(BITRATE.VIDEO_REDUCED);
  } else if (quality === 'good' && prevQuality !== 'good') {
    // Gradual restore: go to reduced first, then max after 10s if still good
    applyVideoBitrate(BITRATE.VIDEO_REDUCED);
    setTimeout(() => {
      if (!_tearingDown && _lastQuality === 'good') {
        applyVideoBitrate(BITRATE.VIDEO_MAX);
      }
    }, 10000);
  }
}

/**
 * Set up audio visualizer for the local stream.
 * Returns a cancel function stored in state.
 */
export function setupAudioVisualizer(levelElement) {
  const state = getMutableState();
  if (!state.localStream || state.localStream.getAudioTracks().length === 0) return;

  try {
    let audioCtx = state.audioContext;
    if (!audioCtx) {
      audioCtx = new AudioContext();
      update({ audioContext: audioCtx });
    }

    const analyser = audioCtx.createAnalyser();
    const source = audioCtx.createMediaStreamSource(state.localStream);
    source.connect(analyser);
    analyser.fftSize = 512;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    let rafId = null;
    let cancelled = false;
    let lastVolume = 0;
    let lastFrameTime = 0;

    function tick(timestamp) {
      if (cancelled) return;
      if (timestamp - lastFrameTime < 33) {
        rafId = requestAnimationFrame(tick);
        return;
      }
      lastFrameTime = timestamp;
      analyser.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128 - 1;
        sum += v * v;
      }
      const volume = Math.sqrt(sum / bufferLength);
      if (Math.abs(volume - lastVolume) > 0.02) {
        lastVolume = volume;
        levelElement.style.height = `${Math.min(100, volume * 200)}%`;
      }
      rafId = requestAnimationFrame(tick);
    }
    tick(0);

    const cancelFn = () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      try {
        source.disconnect();
      } catch {}
    };
    update({ visualizerCancelFn: cancelFn });
  } catch (err) {
    console.warn('[rtc] Audio visualizer failed to start:', err);
  }
}

/**
 * Cancel the active audio visualizer.
 */
export function cancelVisualizer() {
  const state = getMutableState();
  if (state.visualizerCancelFn) {
    try {
      state.visualizerCancelFn();
    } catch {}
    update({ visualizerCancelFn: null });
  }
}

/**
 * Start screen sharing. Replaces the video track on the peer connection.
 * Stores the original video track so it can be restored.
 */
export async function startScreenShare() {
  const state = getMutableState();
  if (!state.peerConnection) return false;

  try {
    const screenStream = await navigator.mediaDevices.getDisplayMedia({
      video: { cursor: 'always' },
      audio: false,
    });
    const screenTrack = screenStream.getVideoTracks()[0];

    // Store original video track for restoration
    const videoSender = state.peerConnection.getSenders().find((s) => s.track?.kind === 'video');
    if (videoSender && state.localStream) {
      state.originalVideoTrack = state.localStream.getVideoTracks()[0];
      await videoSender.replaceTrack(screenTrack);
    }

    // Update local video to show screen
    if (state.localStream && DOM_localVideo) {
      // We keep the localStream as-is; just show screen in local video
      const newStream = new MediaStream();
      newStream.addTrack(screenTrack);
      state.localStream.getAudioTracks().forEach((t) => newStream.addTrack(t));
      DOM_localVideo.srcObject = newStream;
    }

    update({ isScreenSharing: true, screenStream });

    // Handle user stopping share via browser UI
    screenTrack.addEventListener('ended', () => {
      stopScreenShare();
    });

    return true;
  } catch (err) {
    console.warn('[rtc] Screen share failed:', err);
    return false;
  }
}

/**
 * Stop screen sharing and restore the original camera video track.
 */
export async function stopScreenShare() {
  const state = getMutableState();

  if (state.screenStream) {
    state.screenStream.getTracks().forEach((t) => {
      try {
        t.stop();
      } catch {}
    });
  }

  if (state.peerConnection && state.originalVideoTrack) {
    const videoSender = state.peerConnection.getSenders().find((s) => s.track?.kind === 'video');
    if (videoSender) {
      try {
        await videoSender.replaceTrack(state.originalVideoTrack);
      } catch {}
    }
  }

  // Restore local video
  if (state.localStream && DOM_localVideo) {
    DOM_localVideo.srcObject = state.localStream;
  }

  update({ isScreenSharing: false, screenStream: null, originalVideoTrack: null });
}

/**
 * Set the local video element reference for screen share display.
 */
export function setLocalVideoRef(el) {
  DOM_localVideo = el;
}

// --- File Transfer ---

/**
 * Set callback for file transfer events.
 * @param {{ onFileProgress?: Function, onFileComplete?: Function }} callbacks
 */
export function setFileCallbacks({ onFileProgress, onFileComplete, onFileRequest }) {
  _onFileProgress = onFileProgress;
  _onFileComplete = onFileComplete;
  _onFileRequest = onFileRequest;
}

/**
 * Initiate a file transfer by sending file-start and waiting for peer acceptance.
 * Chunk sending begins only after the peer sends file-accept.
 * @param {File} file
 */
export async function sendFile(file) {
  const state = getMutableState();
  if (!state.dataChannel || state.dataChannel.readyState !== 'open') {
    showNotification('No active connection to send file', 'error');
    return;
  }

  if (file.size > PROTOCOL.MAX_FILE_SIZE) {
    showNotification(`File too large (max ${PROTOCOL.MAX_FILE_SIZE / 1024 / 1024 / 1024}GB)`, 'error');
    return;
  }

  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  _pendingFileSends.set(id, { name: file.name, size: file.size, file });

  sendData({
    type: 'file-start',
    id,
    name: file.name.slice(0, PROTOCOL.MAX_FILE_NAME_LENGTH),
    size: file.size,
  });

  if (_onFileProgress)
    _onFileProgress({
      id,
      name: file.name,
      size: file.size,
      direction: 'out',
      progress: 0,
      status: 'pending',
    });
}

/**
 * Start sending file chunks after the peer accepts the transfer.
 * @param {string} id
 */
async function _startSendingChunks(id) {
  const pending = _pendingFileSends.get(id);
  if (!pending) return;

  const { file } = pending;
  const state = getMutableState();
  const chunkSize = PROTOCOL.FILE_CHUNK_SIZE;
  const totalChunks = Math.ceil(file.size / chunkSize);

  if (_onFileProgress)
    _onFileProgress({
      id,
      name: file.name,
      size: file.size,
      direction: 'out',
      progress: 0,
      status: 'sending',
    });

  for (let seq = 0; seq < totalChunks; seq++) {
    const start = seq * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);
    const buf = await chunk.arrayBuffer();
    const base64 = arrayBufferToBase64(buf);

    while (state.dataChannel.bufferedAmount > 1_000_000) {
      await new Promise((r) => setTimeout(r, 50));
    }

    if (state.dataChannel.readyState !== 'open') {
      _pendingFileSends.delete(id);
      if (_onFileProgress)
        _onFileProgress({
          id,
          name: file.name,
          size: file.size,
          direction: 'out',
          progress: Math.round((seq / totalChunks) * 100),
          status: 'rejected',
        });
      return;
    }

    sendData({ type: 'file-chunk', id, seq, data: base64 });

    const progress = Math.round(((seq + 1) / totalChunks) * 100);
    if (_onFileProgress)
      _onFileProgress({
        id,
        name: file.name,
        size: file.size,
        direction: 'out',
        progress,
        status: 'sending',
      });
  }

  sendData({ type: 'file-end', id });
  _pendingFileSends.delete(id);
  if (_onFileProgress)
    _onFileProgress({
      id,
      name: file.name,
      size: file.size,
      direction: 'out',
      progress: 100,
      status: 'done',
    });
}

/**
 * Respond to an incoming file transfer request (accept or deny).
 * Called by app.js when the user clicks accept/deny on the file request prompt.
 * @param {string} id — file transfer id
 * @param {boolean} accepted
 */
export function respondFileRequest(id, accepted) {
  const file = _incomingFiles.get(id);
  if (!file) return;

  if (accepted) {
    file.accepted = true;
    sendData({ type: 'file-accept', id });
    if (_onFileProgress)
      _onFileProgress({
        id,
        name: file.name,
        size: file.size,
        direction: 'in',
        progress: 0,
        status: 'receiving',
      });
  } else {
    sendData({ type: 'file-deny', id });
    _incomingFiles.delete(id);
  }
}

/**
 * Handle incoming file-related data channel messages.
 * Called from the onmessage handler.
 * @param {object} data — sanitized message from protocol validator
 */
export function handleFileMessage(data) {
  switch (data.type) {
    case 'file-start': {
      _incomingFiles.set(data.id, {
        name: data.name,
        size: data.size,
        chunks: [],
        receivedSize: 0,
        accepted: false,
      });
      if (_onFileRequest)
        _onFileRequest({ id: data.id, name: data.name, size: data.size });
      break;
    }
    case 'file-accept': {
      _startSendingChunks(data.id);
      break;
    }
    case 'file-deny': {
      const pending = _pendingFileSends.get(data.id);
      if (pending) {
        _pendingFileSends.delete(data.id);
        showNotification(`File rejected by peer: ${pending.name}`, 'info');
        if (_onFileProgress)
          _onFileProgress({
            id: data.id,
            name: pending.name,
            size: pending.size,
            direction: 'out',
            progress: 0,
            status: 'rejected',
          });
      }
      break;
    }
    case 'file-chunk': {
      const file = _incomingFiles.get(data.id);
      if (!file || !file.accepted) return;
      const buf = base64ToArrayBuffer(data.data);
      file.chunks.push(buf);
      file.receivedSize += buf.byteLength;
      const progress = Math.round((file.receivedSize / file.size) * 100);
      if (_onFileProgress)
        _onFileProgress({
          id: data.id,
          name: file.name,
          size: file.size,
          direction: 'in',
          progress,
          status: 'receiving',
        });
      break;
    }
    case 'file-end': {
      const file = _incomingFiles.get(data.id);
      if (!file || !file.accepted) return;
      const blob = new Blob(file.chunks);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
      _incomingFiles.delete(data.id);
      if (_onFileProgress)
        _onFileProgress({
          id: data.id,
          name: file.name,
          size: file.size,
          direction: 'in',
          progress: 100,
          status: 'done',
        });
      if (_onFileComplete) _onFileComplete({ name: file.name, size: file.size });
      break;
    }
  }
}

/**
 * Convert ArrayBuffer to base64 string.
 */
function arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * Convert base64 string to ArrayBuffer.
 */
function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  const CHUNK = 0x8000;
  for (let i = 0; i < len; i += CHUNK) {
    const end = Math.min(i + CHUNK, len);
    for (let j = i; j < end; j++) {
      bytes[j] = binary.charCodeAt(j);
    }
  }
  return bytes.buffer;
}

/**
 * Full teardown — close connections, stop streams, depart room.
 * Idempotent and safe to call multiple times.
 */
export async function teardown() {
  _tearingDown = true;
  const state = getMutableState();

  // Cancel visualizer
  cancelVisualizer();

  // Stop quality monitor
  stopQualityMonitor();

  // Clear any pending ICE restart
  clearRestartTimer();

  // Reset bandwidth adaptation state
  _lastQuality = 'good';
  _currentVideoBitrate = BITRATE.VIDEO_MAX;
  _restartAttempts = 0;
  _roomListenerSetup = null;

  // Stop screen share if active
  if (state.screenStream) {
    state.screenStream.getTracks().forEach((t) => {
      try {
        t.stop();
      } catch {}
    });
  }

  // Clear incoming file transfers
  _incomingFiles.clear();
  _pendingFileSends.clear();

  // Close data channel
  if (state.dataChannel) {
    try {
      state.dataChannel.close();
    } catch {}
  }

  // Close peer connection
  if (state.peerConnection) {
    try {
      state.peerConnection.close();
    } catch {}
  }

  // Stop local media tracks
  if (state.localStream) {
    state.localStream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {}
    });
  }

  // Depart room (unsub listeners, stop heartbeat, mark ended)
  signaling.depart(state.roomId);

  // Close audio context
  if (state.audioContext) {
    try {
      await state.audioContext.close();
    } catch {}
  }

  _tearingDown = false;
}
